// DevotionalExplanation.tsx — Digital Sanctuary Design System
// The "Reflection" card: scripture insight displayed after answering a quiz question.
// Per Stitch spec: surface_container_low bg, generous padding, no dividers.

import { Ionicons } from '@expo/vector-icons';
import React, { useCallback } from 'react';
import { View, StyleSheet, TouchableOpacity, Linking } from 'react-native';

import { DSText } from '../../design-system/components/DSText';
import { useAppTheme } from '../../design-system/ThemeProvider';
import { spacing, radius, shadows } from '../../design-system/tokens';
import { useLanguageStore } from '../../features/quiz-core/languageStore';
import { t } from '../../i18n/translations';

interface DevotionalExplanationProps {
  explanation: string;
  verseReference?: string;
}

/**
 * Parses a verse reference from the explanation text.
 * Looks for patterns like "(John 3:16)", "- Genesis 1:1", "Matthew 5:3-12", etc.
 */
const extractVerseReference = (text: string): { body: string; verse: string | null } => {
  const versePatterns = [
    /\s*\((\d?\s?[A-Z][a-z]+(?:\s[A-Z]?[a-z]*)?\s+\d+:\d+(?:-\d+)?)\)\s*$/i,
    /\s*[-–—]\s*(\d?\s?[A-Z][a-z]+(?:\s[A-Z]?[a-z]*)?\s+\d+:\d+(?:-\d+)?)\s*$/i,
    /\n(\d?\s?[A-Z][a-z]+(?:\s[A-Z]?[a-z]*)?\s+\d+:\d+(?:-\d+)?)\s*$/i,
  ];

  for (const pattern of versePatterns) {
    const match = text.match(pattern);
    if (match) {
      return {
        body: text.replace(pattern, '').trim(),
        verse: match[1].trim(),
      };
    }
  }

  return { body: text, verse: null };
};

const DevotionalExplanation: React.FC<DevotionalExplanationProps> = ({
  explanation,
  verseReference: verseReferenceProp,
}) => {
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const { language } = useLanguageStore();

  const { body, verse: parsedVerse } = extractVerseReference(String(explanation || ''));
  const verseReference = verseReferenceProp || parsedVerse;

  const handleDigDeeper = useCallback(() => {
    if (!verseReference) return;
    const query = encodeURIComponent(verseReference);
    Linking.openURL(`https://www.biblegateway.com/passage/?search=${query}&version=NIV`);
  }, [verseReference]);

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.iconContainer}>
          <Ionicons name="book-outline" size={18} color={colors.accentDark} />
        </View>
        <DSText variant="label" color={colors.accentDark} uppercase>
          {t('quizFlow.insight', language) || 'Reflection'}
        </DSText>
      </View>

      {/* Verse reference badge */}
      {verseReference && (
        <View style={styles.verseBadge}>
          <Ionicons name="bookmark" size={14} color={colors.accentDark} style={styles.verseIcon} />
          <DSText variant="label" color={colors.onAccentContainer} style={styles.verseText}>
            {verseReference}
          </DSText>
        </View>
      )}

      {/* Explanation body */}
      <DSText variant="body" color={colors.onAccentContainer} style={styles.bodyText}>
        {body}
      </DSText>

      {/* Dig deeper link */}
      {verseReference && (
        <TouchableOpacity
          style={styles.digDeeperLink}
          onPress={handleDigDeeper}
          activeOpacity={0.7}
          accessibilityRole="link"
          accessibilityLabel={t('quizFlow.digDeeper', language)}
        >
          <Ionicons name="open-outline" size={14} color={colors.accentDark} style={{ marginRight: spacing.sm - 2 }} />
          <DSText variant="caption" color={colors.accentDark} style={styles.digDeeperText}>
            {t('quizFlow.digDeeper', language)}
          </DSText>
        </TouchableOpacity>
      )}
    </View>
  );
};

const createStyles = (colors: any) => StyleSheet.create({
  container: {
    backgroundColor: colors.accentContainer,
    borderRadius: radius.lg,
    padding: spacing.xl,
    marginTop: spacing.lg,
    // Ghost border per Stitch No-Line rule
    borderWidth: 1,
    borderColor: 'rgba(199, 196, 216, 0.15)',
    ...(shadows.sm as any),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  iconContainer: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: 'rgba(217, 119, 6, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.sm + 2,
  },
  verseBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(217, 119, 6, 0.08)',
    borderRadius: radius.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginBottom: spacing.md + 2,
    alignSelf: 'flex-start',
  },
  verseIcon: {
    marginRight: spacing.sm - 2,
  },
  verseText: {
    fontWeight: '700',
    fontStyle: 'italic',
  },
  bodyText: {
    lineHeight: 24,
  },
  digDeeperLink: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    marginTop: spacing.md + 2,
    paddingVertical: spacing.sm - 2,
    paddingHorizontal: spacing.sm + 2,
    borderRadius: radius.sm,
    backgroundColor: 'rgba(217, 119, 6, 0.08)',
  },
  digDeeperText: {
    fontWeight: '600',
  },
});

export default DevotionalExplanation;
